import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import MultiSelect from "@/components/MultiSelect";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Pencil } from "lucide-react";

export default function AdminApprovals() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(null); // request object being edited
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/requests", { params: { status: tab } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    api.get("/filters/options").then((r) => setOptions(r.data));
  }, []);

  const openApproval = (req) => {
    setEditing(req);
    setForm({
      data_type: req.data_type,
      gst_coverage: req.gst_coverage ? { ...req.gst_coverage } : { type: "pan_india", states: [], cities: [] },
      mca_coverage: req.mca_coverage ? { ...req.mca_coverage } : { type: "pan_india", states: [], districts: [], rocs: [] },
      days: req.duration_days || 30,
    });
  };

  const submitApprove = async () => {
    try {
      const payload = { data_type: form.data_type, days: Number(form.days) || 30 };
      if (form.data_type !== "mca") payload.gst_coverage = form.gst_coverage;
      if (form.data_type !== "gst") payload.mca_coverage = form.mca_coverage;
      await api.post(`/admin/requests/${editing.id}/approve`, payload);
      toast.success(`Request approved for ${payload.days} day(s). Customer notified.`);
      setEditing(null);
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const doReject = async (id) => {
    try {
      await api.post(`/admin/requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const gstCityOpts = useMemo(() => {
    if (!options || !form?.gst_coverage?.states) return [];
    const s = new Set();
    form.gst_coverage.states.forEach((st) => (options.gst_state_cities[st] || []).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [options, form]);

  const mcaDistrictOpts = useMemo(() => {
    if (!options || !form?.mca_coverage?.states) return [];
    const s = new Set();
    form.mca_coverage.states.forEach((st) => (options.mca_state_districts[st] || []).forEach((d) => s.add(d)));
    return Array.from(s).sort();
  }, [options, form]);

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Approvals</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Review, edit filters, then approve or reject subscription requests.</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="approvals-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="no-approvals">No {tab} requests</Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`approval-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-heading font-semibold text-lg">{r.user_name} <span className="text-muted-foreground font-normal">· {r.user_company}</span></div>
                  <div className="text-xs num text-muted-foreground mt-0.5">{r.user_email} · Submitted {new Date(r.created_at).toLocaleString()}</div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Badge variant="secondary">{r.data_type.toUpperCase()}</Badge>
                    {r.gst_coverage && (
                      <Badge variant="outline">GST · {r.gst_coverage.type.replace("_", " ")}
                        {r.gst_coverage.states?.length > 0 && ` · ${r.gst_coverage.states.length} state(s)`}
                        {r.gst_coverage.cities?.length > 0 && ` · ${r.gst_coverage.cities.length} city(ies)`}
                      </Badge>
                    )}
                    {r.mca_coverage && (
                      <Badge variant="outline">MCA · {r.mca_coverage.type.replace("_", " ")}
                        {r.mca_coverage.states?.length > 0 && ` · ${r.mca_coverage.states.length} state(s)`}
                        {r.mca_coverage.districts?.length > 0 && ` · ${r.mca_coverage.districts.length} district(s)`}
                        {r.mca_coverage.rocs?.length > 0 && ` · ${r.mca_coverage.rocs.length} ROC(s)`}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {tab === "pending" ? (
                    <>
                      <Button variant="outline" onClick={() => openApproval(r)} data-testid={`edit-approve-${r.id}`}>
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit & approve
                      </Button>
                      <Button variant="destructive" onClick={() => doReject(r.id)} data-testid={`reject-${r.id}`}>
                        <XCircle className="h-4 w-4 mr-1.5" /> Reject
                      </Button>
                    </>
                  ) : tab === "approved" ? (
                    <div className="text-xs num text-muted-foreground text-right">
                      Approved: {r.approved_at && new Date(r.approved_at).toLocaleString()}<br />
                      Expires: {r.expires_at && new Date(r.expires_at).toLocaleString()}
                    </div>
                  ) : (
                    <Badge variant="destructive">Rejected</Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(null); } }}>
        <DialogContent className="max-w-3xl bg-popover" data-testid="approve-modal">
          <DialogHeader>
            <DialogTitle>Edit filters & approve — {editing?.user_name}</DialogTitle>
            <DialogDescription>
              Adjust the customer's coverage before approving. This activates a 30-day subscription starting today.
            </DialogDescription>
          </DialogHeader>
          {form && options && (
            <div className="space-y-4">
              <div>
                <Label>Data type</Label>
                <RadioGroup value={form.data_type} onValueChange={(v) => setForm({ ...form, data_type: v })} className="grid grid-cols-3 gap-2 mt-2">
                  {["gst", "mca", "both"].map((v) => (
                    <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.data_type === v ? "border-primary bg-primary/5" : "border-border"}`}>
                      <RadioGroupItem value={v} className="hidden" />
                      {v.toUpperCase()}
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="approve-days">Subscription duration (days)</Label>
                <Input
                  id="approve-days"
                  type="number"
                  min={1}
                  max={3650}
                  value={form.days}
                  onChange={(e) => setForm({ ...form, days: e.target.value })}
                  data-testid="approve-days-input"
                />
                <div className="text-xs text-muted-foreground mt-1">Access expires this many days after approval. Default 30.</div>
              </div>

              {form.data_type !== "mca" && (
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">GST coverage</div>
                  <RadioGroup value={form.gst_coverage.type} onValueChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, type: v } })} className="grid grid-cols-3 gap-2 mb-3">
                    {[["pan_india","Pan India"],["state","State"],["city","City"]].map(([v,l]) => (
                      <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.gst_coverage.type === v ? "border-primary bg-primary/5" : "border-border"}`}>
                        <RadioGroupItem value={v} className="hidden" /> {l}
                      </label>
                    ))}
                  </RadioGroup>
                  {form.gst_coverage.type !== "pan_india" && (
                    <>
                      <Label className="text-xs">States</Label>
                      <MultiSelect
                        options={options.gst_states}
                        values={form.gst_coverage.states}
                        onChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, states: v, cities: form.gst_coverage.cities.filter((c) => gstCityOpts.includes(c)) } })}
                        testid="edit-gst-states"
                      />
                      {form.gst_coverage.type === "city" && (
                        <div className="mt-2">
                          <Label className="text-xs">Cities</Label>
                          <MultiSelect
                            options={gstCityOpts}
                            values={form.gst_coverage.cities}
                            onChange={(v) => setForm({ ...form, gst_coverage: { ...form.gst_coverage, cities: v } })}
                            disabled={form.gst_coverage.states.length === 0}
                            testid="edit-gst-cities"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {form.data_type !== "gst" && (
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">MCA coverage</div>
                  <RadioGroup value={form.mca_coverage.type} onValueChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, type: v } })} className="grid grid-cols-4 gap-2 mb-3">
                    {[["pan_india","Pan India"],["state","State"],["district","District"],["roc","ROC"]].map(([v,l]) => (
                      <label key={v} className={`cursor-pointer rounded-md border p-2 text-center text-sm ${form.mca_coverage.type === v ? "border-primary bg-primary/5" : "border-border"}`}>
                        <RadioGroupItem value={v} className="hidden" /> {l}
                      </label>
                    ))}
                  </RadioGroup>
                  {(form.mca_coverage.type === "state" || form.mca_coverage.type === "district") && (
                    <>
                      <Label className="text-xs">States</Label>
                      <MultiSelect
                        options={options.mca_states}
                        values={form.mca_coverage.states}
                        onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, states: v, districts: form.mca_coverage.districts.filter((d) => mcaDistrictOpts.includes(d)) } })}
                        testid="edit-mca-states"
                      />
                      {form.mca_coverage.type === "district" && (
                        <div className="mt-2">
                          <Label className="text-xs">Districts</Label>
                          <MultiSelect
                            options={mcaDistrictOpts}
                            values={form.mca_coverage.districts}
                            onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, districts: v } })}
                            disabled={form.mca_coverage.states.length === 0}
                            testid="edit-mca-districts"
                          />
                        </div>
                      )}
                    </>
                  )}
                  {form.mca_coverage.type === "roc" && (
                    <>
                      <Label className="text-xs">ROCs</Label>
                      <MultiSelect
                        options={options.rocs}
                        values={form.mca_coverage.rocs}
                        onChange={(v) => setForm({ ...form, mca_coverage: { ...form.mca_coverage, rocs: v } })}
                        testid="edit-mca-rocs"
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="approve-cancel">Cancel</Button>
            <Button onClick={submitApprove} data-testid="approve-confirm">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
